import React, { useState } from "react";
import { Dimensions, StyleSheet, View } from "react-native";
import { Picker } from "react-native";
import { Modal, Text, TextInput, TouchableOpacity } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import { addTrackerOrderEnquiry } from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useToast } from "react-native-toast-notifications";
import Spinner from "../../../components/shared/Spinner";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { translateFields, useTranslation } from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";
import { DeviceOrientation } from "../../../components/context/utils/DeviceOrientation";

interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: any;
  order: string;
}

const TrackerEnquiryModal: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
}: any) => {
  const dispatch = useDispatch<any>();
  const { loadingAddEnqiry } = useSelector((state: any) => state.chinaTracker);
  const toast = useToast();
  const { t } = useTranslation();

  // Hook form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = (data: any) => {
    const payload = {
      orderNumber: order.OrderNumber1 ? order.OrderNumber1 : order.PODoc,
      customerName: order.BPName,
      itemCode: order.ItemCode,
      itemName: order.ItemName,
      orderDate: order.OrderDate,
      subject: data.subject,
      priority: data.priority,
      message: data.message,
      key: order.Key,
      enquiryTrackerType: "CHINA_TRACKER",
    };
    dispatch(addTrackerOrderEnquiry(payload))
      .unwrap()
      .then(() =>
        toast.show("Enquiry created successfully", {
          type: "success",
        })
      )
      .catch((err: any) =>
        toast.show(err.message || err || "Error creating enquiry", {
          type: "danger",
        })
      );

    onClose();
  };
  const { isLargeDevice } = DeviceOrientation();
  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <ScrollView contentContainerStyle={styles.scrollContent}>
          <View style={styles.modalContainer}>
            <Text style={styles.title}>{t("askEnquiry")}</Text>

            {loadingAddEnqiry ? (
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{
                  flexDirection: "row",
                  marginTop: "5%",
                  marginBottom: "5%",
                }}
              />
            ) : (
              <>
                <View style={styles.detailsContainer}>
                  <View style={styles.column}>
                    <View style={{ display: "flex", flexDirection: "row" }}>
                      <Text style={styles.label}>
                        {translateFields(
                          "OrderNumber1",
                          t,
                          chinaTrackerTranslationMap
                        )}
                        :
                      </Text>
                      <Text style={styles.value}>{order?.OrderNumber1}</Text>
                    </View>

                    <View style={{ display: "flex", flexDirection: "row" }}>
                      <Text style={styles.label}>
                        {translateFields("Item", t, chinaTrackerTranslationMap)}
                        :
                      </Text>
                      <Text
                        style={[
                          styles.value,
                          { width: isLargeDevice ? 500 : 350 },
                        ]}
                      >
                        {order?.ItemCode + " " + order?.ItemName}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.column}>
                    <View style={{ display: "flex", flexDirection: "row" }}>
                      <Text style={styles.label}>
                        {translateFields(
                          "Customer",
                          t,
                          chinaTrackerTranslationMap
                        )}
                        :
                      </Text>
                      <Text style={styles.value}>{order?.CustomerRef}</Text>
                    </View>
                    <View style={{ display: "flex", flexDirection: "row" }}>
                      <Text style={styles.label}>
                        {translateFields(
                          "OrderPlaced",
                          t,
                          chinaTrackerTranslationMap
                        )}
                        :
                      </Text>
                      <Text style={styles.value}>{order?.OrderDate}</Text>
                    </View>
                  </View>
                </View>

                {/* Dropdowns */}
                <View style={styles.dropdownRow}>
                  <View style={styles.dropdownContainer}>
                    <Text style={styles.inputLabel}>
                      {translateFields(
                        "Subject",
                        t,
                        chinaTrackerTranslationMap
                      )}
                    </Text>
                    <Controller
                      control={control}
                      name="subject"
                      rules={{ required: "Subject is required" }}
                      render={({ field: { onChange, value } }) => (
                        <>
                          <Picker
                            selectedValue={value}
                            onValueChange={onChange}
                            style={styles.picker}
                          >
                            <Picker.Item
                              label={translateFields(
                                "SelectSubject",
                                t,
                                chinaTrackerTranslationMap
                              )}
                              value=""
                            />
                            <Picker.Item
                              label={translateFields(
                                "OrderIssue",
                                t,
                                chinaTrackerTranslationMap
                              )}
                              value="Order Issue"
                            />
                            <Picker.Item
                              label={translateFields(
                                "ProductInquiry",
                                t,
                                chinaTrackerTranslationMap
                              )}
                              value="Product Inquiry"
                            />
                            <Picker.Item
                              label={translateFields(
                                "Other",
                                t,
                                chinaTrackerTranslationMap
                              )}
                              value="Other"
                            />
                          </Picker>
                          {errors.subject && (
                            <Text style={styles.errorText}>
                              {errors.subject.message as string}
                            </Text>
                          )}
                        </>
                      )}
                    />
                  </View>

                  <View style={styles.dropdownContainer}>
                    <Text style={styles.inputLabel}>
                      {translateFields(
                        "Priority",
                        t,
                        chinaTrackerTranslationMap
                      )}
                    </Text>
                    <Controller
                      control={control}
                      name="priority"
                      rules={{ required: "Priority is required" }}
                      render={({ field: { onChange, value } }) => (
                        <>
                          <Picker
                            selectedValue={value}
                            onValueChange={onChange}
                            style={styles.picker}
                          >
                            <Picker.Item
                              label={translateFields(
                                "SelectPriority",
                                t,
                                chinaTrackerTranslationMap
                              )}
                              value=""
                            />
                            <Picker.Item
                              label={translateFields(
                                "Low",
                                t,
                                chinaTrackerTranslationMap
                              )}
                              value="Low"
                            />
                            <Picker.Item
                              label={translateFields(
                                "High",
                                t,
                                chinaTrackerTranslationMap
                              )}
                              value="High"
                            />
                          </Picker>
                          {errors.priority && (
                            <Text style={styles.errorText}>
                              {errors.priority.message as string}
                            </Text>
                          )}
                        </>
                      )}
                    />
                  </View>
                </View>

                {/* Message Input */}
                <Text style={styles.inputLabel}>
                  {translateFields("Message", t, chinaTrackerTranslationMap)}
                </Text>
                <Controller
                  control={control}
                  name="message"
                  rules={{ required: "Message is required" }}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <TextInput
                        style={styles.messageInput}
                        multiline
                        placeholder={translateFields("TypeYourMessage", t, chinaTrackerTranslationMap)}
                        value={value}
                        onChangeText={onChange}
                      />
                      {errors.message && (
                        <Text style={styles.errorText}>
                          {errors.message.message as string}
                        </Text>
                      )}
                    </>
                  )}
                />

                {/* Footer */}
                <View style={styles.footer}>
                  <TouchableOpacity
                    onPress={onClose}
                    style={styles.closeButton}
                  >
                    <Text>{t("close")}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.submitButton}
                    onPress={handleSubmit(onSubmit)}
                  >
                    <Text style={styles.submitText}>{t("submit")}</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};
const { width, height } = Dimensions.get("window");
const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 20,
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: width > 760 ? 45 : 15,
    width: width > 760 ? "86%" : "90%",
    maxWidth: width > 760 ? 1000 : 500,
    borderRadius: 10,
  },
  title: {
    fontSize: width > 760 ? 25 : 20,
    fontWeight: "bold",
    marginBottom: 35,
    textAlign: "center",
  },
  detailsContainer: {
    flexDirection: width > 760 ? "row" : "column",
    justifyContent: "space-between",
    marginBottom: 15,
  },
  column: {
    flex: 1,
    marginBottom: width > 760 ? 0 : 10,
  },
  label: {
    fontWeight: "bold",
    fontSize: width > 760 ? 16 : 14,
    marginBottom: 3,
  },
  value: {
    fontSize: width > 760 ? 16 : 14,
    marginBottom: 10,
    paddingLeft: 4,
  },
  dropdownRow: {
    flexDirection: width > 760 ? "row" : "column",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  dropdownContainer: {
    flex: 1,
    marginRight: width > 760 ? 10 : 0,
    marginBottom: width > 760 ? 0 : 10,
    padding: width > 760 ? 10 : 2,
  },
  inputLabel: {
    fontWeight: "bold",
    marginBottom: 5,
    marginTop: 20,
  },
  picker: {
    borderWidth: 1.5,
    borderColor: "#a6a6a6",
    height: 40,
    backgroundColor: "#fff",
    borderRadius: 10,
  },
  messageInput: {
    height: 100,
    borderWidth: 1.5,
    borderColor: "#a6a6a6",
    padding: 10,
    backgroundColor: "#fff",
    marginBottom: 15,
    borderRadius: 10,
  },
  footer: {
    flexDirection: width > 760 ? "row" : "column",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 15,
  },
  submitButton: {
    backgroundColor: "#000",
    paddingHorizontal: width > 760 ? 20 : 15,
    paddingVertical: 10,
    borderRadius: 5,
    alignSelf: width > 760 ? "flex-end" : "center",
  },
  submitText: {
    color: "#fff",
    fontWeight: "bold",
    textAlign: "center",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    alignSelf: width > 760 ? "flex-start" : "center",
    marginBottom: width > 760 ? 0 : 10,
  },
  errorText: {
    color: "red",
    fontSize: 12,
    marginTop: 5,
  },
});
export default TrackerEnquiryModal;
